/* @flow weak */

import * as React from 'react';
import { Query } from 'react-apollo';
import { isNil } from 'lodash';
import idx from 'idx';
import ErrorReporter from '../../app/components/ErrorReporter';
import { mountModal } from 'lib/modal';
import type {
  ParticipantMessagingState,
  ClassSearchability,
} from 'queries/__generated__/globalGQLTypes';
import CurrentUser from 'auth/CurrentUser';

import GROUP_MEMBERSHIP_REQUESTS_COUNT_QUERY from 'groupMembershipRequests/containers/queries/GroupMembershipRequestsCountQuery.gql';
import CLASS_SETTINGS_QUERY from 'groups/components/queries/ClassSettings.gql';

import ConfirmDialog from 'components/ConfirmDialog';
import GroupOptionsClassSearchabilityCheckbox from './GroupOptionsClassSearchabilityCheckbox';
import GroupOptionsRequestToJoinCheckbox from './GroupOptionsRequestToJoinCheckbox';
import GroupOptionsParticipantMessagingSelect from './GroupOptionsParticipantMessagingSelect';
import GroupOptionsUnder13Checkbox from './GroupOptionsUnder13Checkbox';
import LoadingIndicator from 'components/LoadingIndicator';

import './styles/GroupOptionsForm.scss';

type Props = {
  hasChildren: ?boolean,
  searchable: ?ClassSearchability, // GroupOptionsClassSearchabilityCheckbox > searchabilityAndMessageabilityCheckbox. Soon to be deprecated
  classSearchabilityEnabled: ?ClassSearchability,
  participantMessaging: ?string,
  onChange: ?Function,
  userIsTeacher: boolean,
  hideUnderageNote: ?boolean,
  requestToJoinEnabled: ?boolean,
  groupUuid: ?string,
  showClassSearchability: boolean,
};

type State = {
  query: string,
  searchable: ClassSearchability,
  classSearchabilityEnabled: ClassSearchability,
  requestToJoinEnabled: boolean,
  participantMessaging: ParticipantMessagingState,
};

class GroupOptionsForm extends React.Component<Props, State> {
  static props = this.props;

  static defaultProps = {
    hasChildren: true,
    showClassSearchability: false,
    classSearchabilityEnabled: null,
    searchable: true,
    requestToJoinEnabled: null,
    groupUuid: '',
    participantMessaging: null,
  };

  state = {
    hasChildren: this.props.hasChildren,
    classSearchabilityEnabled: this.props.classSearchabilityEnabled,
    searchable: this.props.searchable,
    requestToJoinEnabled: this.props.requestToJoinEnabled,
    participantMessaging: this.props.participantMessaging,
  };

  componentWillReceiveProps(nextProps) {
    this.setState({
      searchable: nextProps.searchable,
    });
  }

  getValues = () => {
    return {
      has_children: this.props.userIsTeacher && this.state.hasChildren,
      searchable: this.state.searchable,
      classSearchabilityEnabled: this.state.classSearchabilityEnabled,
      request_to_join_enabled: this.state.requestToJoinEnabled,
    };
  };

  toggle = name => {
    this.setState({ [name]: !this.state[name] });

    if (this.props.onChange) {
      this.props.onChange(name, !this.state[name]);
    }
  };

  renderUnder13Checkbox = (checked, isModal) => (
    <GroupOptionsUnder13Checkbox
      checked={checked}
      hasChildren={this.state.hasChildren}
      toggle={this.toggle}
      isModal={isModal}
    />
  );

  renderClassSearchabilityCheckbox = (
    classSearchabilityFromBackend,
    classSearchabilitySchoolDefault,
    currentUser
  ) => {
    const { groupUuid } = this.props;
    const checked = isNil(this.state.classSearchabilityEnabled)
      ? classSearchabilityFromBackend === 'searchable'
      : this.state.classSearchabilityEnabled === 'searchable';

    const classSearchabilityFeatureEnabled =
      currentUser && currentUser.hasFeature('set_class_searchability.web.nov-19');
    return (
      <GroupOptionsClassSearchabilityCheckbox
        groupUuid={groupUuid}
        checked={checked}
        classSearchabilitySchoolDefault={classSearchabilitySchoolDefault}
        toggle={this.toggle}
        classSearchabilityFeatureEnabled={classSearchabilityFeatureEnabled}
        onChange={(event, classSearchabilityMutation) => {
          const searchability = event.target.checked ? 'searchable' : 'unsearchable';
          if (classSearchabilityFeatureEnabled && classSearchabilityMutation) {
            classSearchabilityMutation({
              variables: { uuid: groupUuid, input: { searchability } },
            });
          } else {
            this.toggle('searchable');
          }
          this.setState({ classSearchabilityEnabled: searchability });
        }}
      />
    );
  };

  renderRequestToJoinCheckbox = (
    requestToJoinFromBackend,
    requestToJoinEnabledRecommendation,
    currentUser
  ) => {
    const requestToJoinFromFrontend = this.state.requestToJoinEnabled;
    const requestToJoinEnabled = isNil(requestToJoinFromFrontend)
      ? requestToJoinFromBackend
      : requestToJoinFromFrontend;
    const { groupUuid } = this.props;

    const requestToJoinFeatureEnabled =
      currentUser && currentUser.hasFeature('request_to_join.web.dec-19');
    if (requestToJoinFeatureEnabled) {
      return (
        <Query
          query={GROUP_MEMBERSHIP_REQUESTS_COUNT_QUERY}
          variables={{ groupUuid }}
          fetchPolicy="network-only"
          skip={!requestToJoinEnabled}>
          {({ _loading, _error, data }) => {
            const requestCount =
              (data && idx(data, _ => _.classes[0].groupMembershipRequestsCount)) || 0;
            return (
              <GroupOptionsRequestToJoinCheckbox
                groupUuid={groupUuid}
                checked={requestToJoinEnabled}
                requestToJoinEnabledRecommendation={requestToJoinEnabledRecommendation}
                onChange={(e, requestToJoinMutation) => {
                  const enabled = e.target.checked;
                  const toggleRequestToJoin = () => {
                    requestToJoinMutation({ variables: { input: { groupUuid, enabled } } });
                    this.setState({ requestToJoinEnabled: enabled });
                  };

                  if (requestToJoinEnabled && requestCount > 0) {
                    mountModal(
                      this.renderDisableRequestToJoinModal(requestCount, toggleRequestToJoin),
                      this
                    );
                  } else {
                    toggleRequestToJoin();
                  }
                }}
              />
            );
          }}
        </Query>
      );
    } else {
      return null;
    }
  };

  renderParticipantMessagingSelect = (
    participantMessagingFromBackend,
    participantMessagingSchoolDefault,
    currentUser
  ) => {
    const { groupUuid } = this.props;
    const participantMessagingFromFrontend = this.state.participantMessaging;
    const participantMessaging = isNil(participantMessagingFromFrontend)
      ? participantMessagingFromBackend
      : participantMessagingFromFrontend;

    const participantMessagingSelect = (setParticipantMessaging, participantMessagingValue) => {
      setParticipantMessaging({
        variables: {
          uuid: groupUuid,
          input: { participantMessagingState: participantMessagingValue },
        },
      });
      this.setState({ participantMessaging: participantMessagingValue });
    };

    const classSearchabilityFeatureEnabled =
      currentUser && currentUser.hasFeature('set_class_searchability.web.nov-19');
    if (classSearchabilityFeatureEnabled) {
      return (
        <GroupOptionsParticipantMessagingSelect
          groupUuid={groupUuid}
          participantMessagingSchoolDefault={participantMessagingSchoolDefault}
          participantMessaging={participantMessaging}
          participantMessagingSelect={participantMessagingSelect}
        />
      );
    } else {
      return null;
    }
  };

  renderDisableRequestToJoinModal = (count: number, onConfirm: () => void) => {
    return (
      <ConfirmDialog
        title="Approve pending requests?"
        subtext={`There are currently ${count} pending requests to join this class. By unchecking this box, do you want to approve their requests?`}
        cancelButtonText="Cancel"
        actionButtonText={`Approve ${count} pending requests`}
        onConfirm={onConfirm}
      />
    );
  };

  render() {
    const { showClassSearchability, userIsTeacher, groupUuid } = this.props;
    // hideUnderageNote is being passed in as a string for some reason
    const hideUnderageNote = this.props.hideUnderageNote === 'true';

    if (!groupUuid) {
      return this.renderUnder13Checkbox(!hideUnderageNote, true);
    }
    const currentUser = CurrentUser.getUser();

    return (
      <Query query={CLASS_SETTINGS_QUERY} variables={{ groupUuid }} fetchPolicy="network-only">
        {({ loading, _error, data }) => {
          if (loading && (data && !data.classSettingsScreen)) {
            return <LoadingIndicator />;
          } else if (!data || !data.classSettingsScreen) {
            return <ErrorReporter error="GroupOptionsForm - unable to load class settings" />;
          }

          const {
            canEditParticipantMessagingState,
            participantMessagingState,
            participantMessagingSchoolRecommendation,
            canEditSearchability,
            searchability,
            searchabilitySchoolRecommendation,
            requestToJoinEnabled,
            requestToJoinEnabledRecommendation,
            canEditWillOnlyMessage13AndOlder,
          } = data.classSettingsScreen;

          return (
            <React.Fragment>
              {showClassSearchability &&
                canEditSearchability &&
                this.renderClassSearchabilityCheckbox(
                  searchability,
                  searchabilitySchoolRecommendation,
                  currentUser
                )}
              {this.renderRequestToJoinCheckbox(
                requestToJoinEnabled,
                requestToJoinEnabledRecommendation,
                currentUser
              )}
              {canEditParticipantMessagingState &&
                this.renderParticipantMessagingSelect(
                  participantMessagingState,
                  participantMessagingSchoolRecommendation,
                  currentUser
                )}
              {canEditWillOnlyMessage13AndOlder &&
                userIsTeacher &&
                this.renderUnder13Checkbox(!hideUnderageNote)}
            </React.Fragment>
          );
        }}
      </Query>
    );
  }
}

export default GroupOptionsForm;
